import React from 'react';
import { Shield, Users, Eye, Monitor, Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './calculate-incident-level.scss';

const CalculateIncidentLevel = () => {
	const { t } = useTranslation();

	const incidentLevels = [
		{
			level: t('high_risk'),
			points: t('high_risk_points'),
			color: 'red',
			icon: <XCircle className="icon" />,
			description: t('critical_security_breach'),
			type: 'high'
		},
		{
			level: t('medium_risk'),
			points: t('medium_risk_points'),
			color: 'orange',
			icon: <AlertTriangle className="icon" />,
			description: t('moderate_security_concern'),
			type: 'medium'
		},
		{
			level: t('low_risk'),
			points: t('low_risk_points'),
			color: 'green',
			icon: <CheckCircle className="icon" />,
			description: t('minor_security_alert'),
			type: 'low'
		}
	];

	const incidentTypes = [
		{
			title: t('single_person_missing'),
			icon: <Users className="incident-icon" />,
			color: 'blue',
			rules: [
				{ duration: t('duration_0_3_seconds'), points: t('one_point'), severity: 'low' },
				{ duration: t('duration_4_6_seconds'), points: t('three_points'), severity: 'low' },
				{ duration: t('duration_7_15_seconds'), points: t('twenty_five_points'), severity: 'medium' },
				{ duration: t('duration_16_plus_seconds'), points: t('fifty_points'), severity: 'high' }
			]
		},
		{
			title: t('multiple_persons_detected'),
			icon: <Users className="incident-icon" />,
			color: 'purple',
			rules: [
				{ duration: t('duration_0_3_seconds'), points: t('one_point'), severity: 'low' },
				{ duration: t('duration_4_6_seconds'), points: t('five_points'), severity: 'low' },
				{ duration: t('duration_7_15_seconds'), points: t('twenty_five_points'), severity: 'medium' },
				{ duration: t('duration_16_plus_seconds'), points: t('fifty_points'), severity: 'high' }
			]
		},
		{
			title: t('object_detection'),
			icon: <Eye className="incident-icon" />,
			color: 'indigo',
			rules: [
				{ duration: t('duration_0_3_seconds'), points: t('three_points'), severity: 'low' },
				{ duration: t('duration_4_10_seconds'), points: t('fifteen_points'), severity: 'medium' },
				{ duration: t('duration_11_plus_seconds'), points: t('fifty_points'), severity: 'high' }
			]
		},
		{
			title: t('away_from_exam_page'),
			icon: <Monitor className="incident-icon" />,
			color: 'teal',
			rules: [
				{ duration: t('duration_0_3_seconds'), points: t('three_points'), severity: 'low' },
				{ duration: t('duration_4_10_seconds'), points: t('twenty_five_points'), severity: 'medium' },
				{ duration: t('duration_11_plus_seconds'), points: t('fifty_points'), severity: 'high' }
			]
		}
	];

	const features = [
		{
			icon: <Shield className="feature-icon" />,
			title: t('real_time_detection'),
			description: t('continuous_monitoring_threat')
		},
		{
			icon: <Users className="feature-icon" />,
			title: t('person_tracking'),
			description: t('facial_recognition_verification')
		},
		{
			icon: <Eye className="feature-icon" />,
			title: t('object_recognition'),
			description: t('intelligent_detection_materials')
		},
		{
			icon: <Monitor className="feature-icon" />,
			title: t('detect_unfocus'),
			description: t('screen_surveillance_tracking')
		}
	];

	return (
		<div className="proctoring-guide">
			<div className="proctoring-guide__container">
				<div className="proctoring-guide__header">
					<h1 className="proctoring-guide__title">{t('ai_proctoring_incident_guide')}</h1>
					<p className="proctoring-guide__description">{t('comprehensive_incident_classification')}</p>
				</div>

				<section className="section">
					<div className="section__header">
						<h2 className="section__title">{t('incident_risk_levels')}</h2>
						<p className="section__description">{t('security_incidents_classified')}</p>
					</div>

					<div className="risk-levels">
						{incidentLevels.map((level, index) => (
							<div key={index} className={`risk-levels__card risk-levels__card--${level.color}`}>
								<div className="risk-levels__stripe"></div>
								<div className="risk-levels__header">
									<div className="risk-levels__icon-bg">{level.icon}</div>
									<div className={`risk-levels__points risk-levels__points--${level.type}`}>
										{level.points}
									</div>
								</div>
								<h3 className="risk-levels__title">{level.level}</h3>
								<p className="risk-levels__description">{level.description}</p>
							</div>
						))}
					</div>
				</section>

				<section className="section">
					<div className="section__header">
						<h2 className="section__title">{t('scoring_rules_point_system')}</h2>
						<p className="section__description">{t('detailed_breakdown_points')}</p>
					</div>

					<div className="incident-types">
						{incidentTypes.map((type, index) => (
							<div key={index} className={`incident-types__card incident-types__card--${type.color}`}>
								<div className="incident-types__header">
									<div className="incident-types__icon-bg">{type.icon}</div>
									<h3 className="incident-types__title">{type.title}</h3>
								</div>

								<div className="incident-types__rules">
									{type.rules.map((rule, ruleIndex) => (
										<div key={ruleIndex} className="incident-types__rule">
											<div className="incident-types__rule-info">
												<Clock className="clock-icon" />
												<span className="incident-types__duration">{rule.duration}</span>
											</div>
											<div className={`incident-types__points incident-types__points--${rule.severity}`}>
												{rule.points}
											</div>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</section>

				<section className="section">
					<div className="section__header">
						<h2 className="section__title">{t('system_features')}</h2>
						<p className="section__description">{t('advanced_ai_monitoring')}</p>
					</div>

					<div className="features">
						{features.map((feature, index) => (
							<div key={index} className="features__card">
								<div className="features__icon-bg">{feature.icon}</div>
								<h3 className="features__title">{feature.title}</h3>
								<p className="features__description">{feature.description}</p>
							</div>
						))}
					</div>
				</section>
			</div>
		</div>
	);
};

export default CalculateIncidentLevel;
